import { useState, useEffect } from 'react'
import styles from './CaseDetail.module.css'
import { API_BASE_URL, getImageUrl } from '../config'
import LockForm from './LockForm'

interface CaseData {
  id: number
  title: string
  logo: string
  thumbnail: string
  hero_title: string
  hero_content: string
  hero_bg: string
  content: string
  pdf_images: string[]
  pdf_url?: string
  pdf_download_url?: string
  industry_name: string
  region_name: string
}

interface CaseDetailProps {
  isUnlocked: boolean
  onUnlock: () => void
  caseId?: number
  type?: 'case' | 'report'
  onPdfUrlLoaded?: (url: string) => void
}

const CaseDetail = ({ isUnlocked, onUnlock, caseId, type = 'case', onPdfUrlLoaded }: CaseDetailProps) => {
  const [caseData, setCaseData] = useState<CaseData | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!caseId) return

    const fetchCaseDetail = async () => {
      setLoading(true)
      try {
        const endpoint = type === 'report'
          ? `${API_BASE_URL}/api/payment-reports/${caseId}`
          : `${API_BASE_URL}/api/cases/${caseId}`
        const response = await fetch(endpoint)
        const result = await response.json()
        if (result.success) {
          const data = result.data
          if (data.pdf_images && typeof data.pdf_images === 'string') {
            try {
              data.pdf_images = JSON.parse(data.pdf_images)
            } catch (e) {
              if (type !== 'report') {
                data.pdf_images = []
              }
            }
          }
          setCaseData(data)
          if (type === 'report' && data.pdf_url) {
            onPdfUrlLoaded?.(data.pdf_url)
          } else if (type === 'case' && data.content) {
            onPdfUrlLoaded?.(data.content)
          }
        }
      } catch (error) {
        console.error('Failed to fetch case detail:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchCaseDetail()
  }, [caseId, type])

  return (
    <div className={styles.container}>
      {loading && (
        <div className={styles.loading}>加载中...</div>
      )}

      {/* Hero Section - Blue gradient with tags and title */}
      {caseData && (
        <div className={styles.hero}>
          <div
            className={styles.heroBg}
            style={caseData.hero_bg ? { backgroundImage: `url(${getImageUrl(caseData.hero_bg)})` } : undefined}
          ></div>
          <div className={styles.heroContent}>
            {type !== 'report' && (
              <div className={styles.tags}>
                <span className={styles.tag}>数娱行业</span>
                <span className={styles.tag}>游戏</span>
              </div>
            )}
            <h1 className={styles.heroTitle}>
              {caseData.hero_title}
              {type === 'report' && caseData.region_name && (
                <span className={styles.heroRegion}><span className={styles.heroRegionTag}>{caseData.region_name}</span></span>
              )}
            </h1>
            <p className={styles.heroDescription}>
              {caseData.hero_content}
            </p>
          </div>
        </div>
      )}

      {/* Main Content - White background with PDF and Form */}
      <div className={styles.contentWrapper}>
        {/* PDF Preview Section - stop at __LOCK__ marker */}
        {type === 'report' && caseData?.pdf_images && (() => {
          const images = Array.isArray(caseData.pdf_images) ? caseData.pdf_images : [caseData.pdf_images]
          const lockIdx = images.indexOf('__LOCK__')
          const visible = lockIdx !== -1 ? images.slice(0, lockIdx) : images
          if (visible.length === 0) return null
          return (
            <div className={styles.pdfCard}>
              <img
                src={getImageUrl(visible[0])}
                alt="报告页面 1"
                className={styles.pdfImage}
              />
            </div>
          )
        })()}

        {/* Case PDF Images - stop at __LOCK__ marker */}
        {type === 'case' && caseData?.pdf_images && Array.isArray(caseData.pdf_images) && caseData.pdf_images.length > 0 && (() => {
          const images = caseData.pdf_images as string[]
          const lockIdx = images.indexOf('__LOCK__')
          const visible = lockIdx !== -1 ? images.slice(0, lockIdx) : images
          if (visible.length === 0) return null
          return (
            <div className={styles.pdfCard}>
              {visible.map((url: string, index: number) => (
                <img key={index} src={getImageUrl(url)} alt={`PDF 页面 ${index + 1}`} className={styles.pdfImage} />
              ))}
            </div>
          )
        })()}

        {/* Form Section - using LockForm component */}
        <LockForm
          isUnlocked={isUnlocked}
          onUnlock={onUnlock}
          caseId={caseId}
          type={type}
          pdfDownloadUrl={getImageUrl(caseData?.pdf_download_url || caseData?.pdf_url || '')}
        />

        {/* Unlocked Rich Text Content - only for case type */}
        {type !== 'report' && isUnlocked && caseData?.content && (
          <div className={styles.richContent}>
            {caseData.content.split('\n').map((line, index) => {
              // Check if it's an image
              const imageMatch = line.match(/!\[(.*?)\]\((.*?)\)/)
              if (imageMatch) {
                return <img key={index} src={getImageUrl(imageMatch[2])} alt={imageMatch[1]} className={styles.contentImage} />
              }
              if (!line.trim()) return null
              // Check if it's a heading (starts with number followed by period)
              if (/^\d+\./.test(line.trim())) {
                return <h3 key={index} className={styles.contentHeading}>{line}</h3>
              }
              return <p key={index} className={styles.contentParagraph}>{line}</p>
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default CaseDetail
